import { resolveSidecarApiUrl } from "~/services/calling/config";
import type { IncomingCallInfo } from "~/services/calling/types";

export type QueueableCall = IncomingCallInfo & {
  uniqueId: string;
};

type SidecarCallRecord = {
  uniqueId?: unknown;
  callId?: unknown;
  status?: unknown;
  callerNumber?: unknown;
  callType?: unknown;
  deviceCid?: unknown;
  startedAt?: unknown;
  customerId?: unknown;
  customerName?: unknown;
  deviceUnitId?: unknown;
};

type SidecarCallsResponse = {
  success?: boolean;
  calls?: SidecarCallRecord[];
};

type SidecarQueueClaimRecord = {
  uniqueId?: unknown;
  extension?: unknown;
  claimedAt?: unknown;
};

type SidecarQueueClaimsResponse = {
  claims?: SidecarQueueClaimRecord[];
};

function asString(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

function parseDateMs(value: unknown) {
  const raw = asString(value);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeStatusToken(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export function mapSidecarRingingToQueueableCall(
  record: SidecarCallRecord
): QueueableCall | null {
  const uniqueId = asString(record.uniqueId);
  if (!uniqueId) return null;
  if (normalizeStatusToken(record.status) !== "ringing") return null;

  const receivedAtMs = parseDateMs(record.startedAt) ?? Date.now();
  const service =
    asString(record.callType) ?? asString(record.deviceCid) ?? null;

  return {
    id: uniqueId,
    uniqueId,
    callUuid: uniqueId,
    callId: asString(record.callId) ?? uniqueId,
    callerNumber: asString(record.callerNumber) ?? null,
    customerId: asString(record.customerId) ?? null,
    customerName: asString(record.customerName) ?? null,
    unitId: asString(record.deviceUnitId) ?? null,
    service,
    description: "Voice Call",
    signalType: null,
    requiresAnswer: null,
    metadata: null,
    receivedAtMs,
  };
}

export function mapSnapshotRingingToQueueableCall(entry: {
  uniqueId: string;
  caller?: string | null;
  callType?: string | null;
  deviceCid?: string | null;
  startedAt?: string | null;
}): QueueableCall {
  const receivedAtMs = parseDateMs(entry.startedAt) ?? Date.now();
  return {
    id: entry.uniqueId,
    uniqueId: entry.uniqueId,
    callUuid: entry.uniqueId,
    callId: entry.uniqueId,
    callerNumber: entry.caller ?? null,
    customerId: null,
    customerName: null,
    service: entry.callType ?? entry.deviceCid ?? null,
    description: "Voice Call",
    signalType: null,
    requiresAnswer: null,
    metadata: null,
    receivedAtMs,
  };
}

async function fetchSidecarJson<T>(path: string): Promise<T> {
  const base = resolveSidecarApiUrl();
  if (!base) throw new Error("Sidecar API URL not configured.");

  const url = `${base.replace(/\/$/, "")}${path}`;
  const res = await fetch(url, { method: "GET" });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };

  if (!res.ok) {
    throw new Error(
      typeof data.error === "string"
        ? data.error
        : `Sidecar API ${path} failed: ${res.status}`
    );
  }

  return data;
}

/** Live ringing calls from sidecar call registry (authoritative for queue/claim). */
export async function fetchQueueableCalls(): Promise<QueueableCall[]> {
  const data = await fetchSidecarJson<SidecarCallsResponse>("/calls");
  const records = Array.isArray(data.calls) ? data.calls : [];

  return records
    .map(mapSidecarRingingToQueueableCall)
    .filter((entry): entry is QueueableCall => Boolean(entry))
    .sort((a, b) => (b.receivedAtMs ?? 0) - (a.receivedAtMs ?? 0));
}

export type ActiveQueueClaim = {
  uniqueId: string;
  extension: string;
  claimedAt: string | null;
};

export async function fetchActiveQueueClaims(): Promise<ActiveQueueClaim[]> {
  const data = await fetchSidecarJson<SidecarQueueClaimsResponse>("/queue/claims");
  const records = Array.isArray(data.claims) ? data.claims : [];

  return records
    .map((record) => {
      const uniqueId = asString(record.uniqueId);
      const extension = asString(record.extension);
      if (!uniqueId || !extension) return null;
      return {
        uniqueId,
        extension,
        claimedAt: asString(record.claimedAt) ?? null,
      };
    })
    .filter((entry): entry is ActiveQueueClaim => Boolean(entry));
}
